// Right-side slide-in drawer for any tracking row.
//
// Used by both In-flight and Exceptions tabs — same component, same
// shape. Renders order summary + recipient + TrackingTimeline. Closes
// on Esc / backdrop click / X button.

import { useEffect } from 'react'
import { X, MapPin, Package, User, Calendar, Truck, Ban } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import TrackingTimeline from '../TrackingTimeline'

function fmtDate(iso) {
  if (!iso) return '—'
  try { return format(parseISO(iso), 'd MMM yyyy') } catch { return iso }
}

function Field({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      {Icon && <Icon size={12} className="text-slate-400 mt-0.5 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{label}</p>
        <p className="text-slate-700 mt-0.5">{children}</p>
      </div>
    </div>
  )
}

export default function OrderDetailDrawer({ row, onClose, getEvents, full = false, canRefresh = true }) {
  // Esc to close — registered while open.
  useEffect(() => {
    if (!row) return
    function onKey(e) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [row, onClose])

  if (!row) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/30 z-40 transition-opacity"
        onClick={onClose}
      />
      {/* Panel */}
      <aside
        className="fixed top-0 right-0 bottom-0 w-full sm:w-[480px] bg-white z-50 shadow-2xl flex flex-col"
        role="dialog"
        aria-label={`Order ${row.order_number} details`}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Order</p>
            <h2 className="text-lg font-bold text-slate-800 leading-tight mt-0.5">
              #{row.order_number || '—'}
            </h2>
            {row.client_name && (
              <p className="text-xs text-slate-500 mt-1">
                {row.client_name}{row.store_name && ` · ${row.store_name}`}
              </p>
            )}
            {/* Explains up-front why the timeline below is empty, so the
                operator doesn't sit waiting for scans that can't arrive. */}
            {row.label_voided && (
              <span className="inline-flex items-center gap-1 mt-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
                <Ban size={10} /> Label voided — replaced
              </span>
            )}
            {row.tracking_status === 'cancelled' && (
              <span className="inline-flex items-center gap-1 mt-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
                <Ban size={10} /> Cancelled by sender
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Order summary grid */}
          <div className="grid grid-cols-2 gap-3">
            <Field icon={Truck}    label="Carrier">{row.carrier || '—'}{row.service && <span className="text-slate-400"> · {row.service}</span>}</Field>
            <Field icon={Package}  label="Tracking">
              {row.tracking_number ? (
                <span className="font-mono text-[11px]">{row.tracking_number}</span>
              ) : '—'}
            </Field>
            <Field icon={Calendar} label="Shipped">{fmtDate(row.shipped_date)}</Field>
            <Field               label="Days in transit">
              {row.days_in_transit != null ? `${row.days_in_transit}d` : '—'}
            </Field>
            <Field icon={User}     label="Recipient">
              {[row.recipient_suburb, row.recipient_state].filter(Boolean).join(', ') || '—'}
              {row.recipient_postcode && <span className="text-slate-400"> · {row.recipient_postcode}</span>}
            </Field>
            <Field               label="Weight">
              {row.weight_kg != null ? `${Number(row.weight_kg).toFixed(2)} kg` : '—'}
            </Field>
          </div>

          {/* Last known scan callout */}
          {row.last_scan_at && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
              <div className="flex items-center gap-1.5 text-slate-500 font-medium mb-0.5">
                <MapPin size={11} /> Last scan
              </div>
              <p className="text-slate-700">{row.last_scan_status || '—'}</p>
              <p className="text-slate-400 mt-0.5">
                {fmtDate(row.last_scan_at)}{row.last_scan_location && ` · ${row.last_scan_location}`}
              </p>
            </div>
          )}

          {/* Full timeline */}
          <TrackingTimeline orderId={row.order_id} getEvents={getEvents} full={full} canRefresh={canRefresh} />
        </div>
      </aside>
    </>
  )
}
